import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus,
  Settings as SettingsIcon,
  LogOut,
  TrendingUp,
  List,
  Lightbulb,
  Users,
  HeartHandshake,
  Footprints,
  ChevronRight,
  Loader2,
  type LucideIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { StreakCard } from "@/components/recovery/streak-card";
import { SobrietyCard } from "@/components/recovery/sobriety-card";
import { ThemeToggle } from "@/components/theme-toggle";
import { STATUS_SHAPES, type StatusKey } from "@/lib/status";
import { signOut } from "@/lib/auth-client";
import {
  ApiError,
  getDashboard,
  listEntries,
  type Dashboard,
  type EntrySummary,
} from "@/lib/api";
import { entryWhen, greetingByHour } from "@/lib/format";

const QUICK_ACTIONS: { icon: LucideIcon; label: string; to?: string }[] = [
  { icon: TrendingUp, label: "Trends", to: "/trends" },
  { icon: List, label: "Lists", to: "/lists" },
  { icon: Lightbulb, label: "Bright Ideas", to: "/bright-ideas" },
  { icon: Users, label: "Sharing", to: "/sharing" },
  { icon: HeartHandshake, label: "Sponsor", to: "/sponsor" },
];

// Calm, honest roll-up labels for the recent-entries list.
const STATUS_LABELS: Record<StatusKey, string> = {
  green: "On Track",
  yellow: "Caution",
  red: "Reach Out",
};

function entryMeta(e: EntrySummary): string {
  const kind = e.mode === "quick" ? "Quick check-in" : "Full check-in";
  if (e.item_count > 0) {
    return `${e.item_count} question${e.item_count === 1 ? "" : "s"} · ${kind}`;
  }
  return kind;
}

export default function Home() {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState<Dashboard | null>(null);
  const [entries, setEntries] = useState<EntrySummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([getDashboard(), listEntries()])
      .then(([d, e]) => {
        // First sign-in → the gentle setup flow, once.
        if (!d.onboarding_completed) {
          navigate("/welcome", { replace: true });
          return;
        }
        setDashboard(d);
        setEntries(e);
      })
      .catch((err) => {
        if (err instanceof ApiError && err.status === 401) navigate("/signin");
        else setError("Couldn't load your dashboard. Please refresh.");
      })
      .finally(() => setLoading(false));
  }, [navigate]);

  const checkedInToday = dashboard?.streak.checked_in_today ?? false;

  return (
    <div className="pb-24">
      {/* Header */}
      <div className="flex items-center justify-between px-6 pb-4 pt-8">
        <div>
          <p className="text-sm text-muted-foreground">{greetingByHour()},</p>
          <h1 className="text-2xl font-bold text-foreground">
            {dashboard?.greeting_name ?? " "}
          </h1>
        </div>
        <div className="flex gap-1">
          <ThemeToggle />
          <Button
            variant="ghost"
            size="icon"
            aria-label="Settings"
            onClick={() => navigate("/settings")}
          >
            <SettingsIcon className="h-5 w-5" strokeWidth={2} />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            aria-label="Sign out"
            onClick={async () => {
              await signOut();
              navigate("/");
            }}
          >
            <LogOut className="h-5 w-5" strokeWidth={2} />
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center gap-2 px-6 py-16 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" strokeWidth={2} />
          <span className="text-sm">Loading…</span>
        </div>
      ) : error ? (
        <p className="px-6 py-16 text-center text-sm text-muted-foreground">{error}</p>
      ) : (
        <div className="flex flex-col gap-3 px-6">
          {dashboard && (
            <StreakCard
              days={dashboard.streak.current}
              bestStreak={dashboard.streak.best}
              checkedInToday={checkedInToday}
            />
          )}
          {dashboard?.sobriety_start_date && (
            <SobrietyCard
              startDate={dashboard.sobriety_start_date}
              label={dashboard.terminology.card}
            />
          )}

          <div className="mt-2">
            <Button size="xl" fullWidth onClick={() => navigate("/checkin")}>
              <Plus className="h-4 w-4" strokeWidth={2} />
              {checkedInToday ? "Add Another Entry" : "Start Today's Check-In"}
            </Button>
            <p className="mt-1.5 text-center text-xs text-muted-foreground">
              Staying aware for the next 24 hours.
            </p>
          </div>

          <div className="mt-1 grid grid-cols-3 gap-3">
            {QUICK_ACTIONS.map(({ icon: Icon, label, to }) => (
              <Card
                key={label}
                interactive={!!to}
                onClick={to ? () => navigate(to) : undefined}
              >
                <CardContent className="flex flex-col items-center gap-2 px-3 py-4 text-center">
                  <Icon
                    className={`h-5 w-5 ${to ? "text-primary" : "text-muted-foreground"}`}
                    strokeWidth={2}
                  />
                  <span
                    className={`text-[13px] font-medium ${to ? "text-foreground" : "text-muted-foreground"}`}
                  >
                    {label}
                  </span>
                  {!to && (
                    <span className="text-[10px] uppercase tracking-[0.05em] text-muted-foreground">
                      Soon
                    </span>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Twelve-step work — a distinct, guided space alongside the daily
              loop, never crammed into it. */}
          <Card interactive className="mt-2" onClick={() => navigate("/steps")}>
            <CardContent className="flex items-center gap-3 px-4 py-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <Footprints className="h-5 w-5" strokeWidth={2} />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-foreground">Twelve-Step Work</p>
                <p className="text-xs text-muted-foreground">
                  Worked in order · at your own pace
                </p>
              </div>
              <ChevronRight className="h-4 w-4 text-muted-foreground" strokeWidth={2} />
            </CardContent>
          </Card>

          <h2 className="mt-5 text-lg font-semibold text-foreground">
            Recent Entries
          </h2>
          {entries.length === 0 ? (
            <Card>
              <CardContent className="px-4 py-6 text-center">
                <p className="text-sm text-muted-foreground">
                  No entries yet. Your first check-in starts here.
                </p>
              </CardContent>
            </Card>
          ) : (
            entries.map((e) => {
              const status = e.overall_status ?? e.mood_status;
              const Shape = status ? STATUS_SHAPES[status] : null;
              return (
                <Card interactive key={e.id} onClick={() => navigate(`/entries/${e.id}`)}>
                  <CardContent className="flex items-center gap-3 px-4 py-3">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-foreground">
                        {entryWhen(e.created_at)}
                      </p>
                      <p className="text-xs text-muted-foreground">{entryMeta(e)}</p>
                    </div>
                    {status && Shape ? (
                      <Badge status={status}>
                        <Shape className="h-[11px] w-[11px]" strokeWidth={2} />
                        {STATUS_LABELS[status]}
                      </Badge>
                    ) : (
                      <span className="text-xs text-muted-foreground">Logged</span>
                    )}
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
